import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';
import Table from '../ui/Table';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Search, Filter, FileDown, ChevronDown, X, Download, FileText } from 'lucide-react';
import Loading from '../shared/Loading';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';

const ConversationsReport = () => {
    const navigate = useNavigate();
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [total, setTotal] = useState(0);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    
    const fetchConversations = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiService.getConversations({
                page,
                limit: pageSize,
                search: searchQuery || undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined
            });
            
            setConversations(response.conversations || []);
            setTotal(response.pagination?.total || 0);
        } catch (err) {
            setError('Failed to load conversations');
            console.error('Error loading conversations:', err);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, searchQuery, startDate, endDate]);

    useEffect(() => {
        fetchConversations();
    }, [fetchConversations]);

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            setPage(1);
            setSearchQuery(e.target.value);
        }
    };

    const clearSearch = () => {
        setSearchQuery('');
        setPage(1);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setPage(1);
    };

    const handleNextPage = () => {
        if (page * pageSize < total) {
            setPage(page + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 1) {
            setPage(page - 1);
        }
    };

    const handleViewConversation = (conversation) => {
        navigate(`/chat?phone=${conversation.phone}`);
    };

    const handleExportExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(conversations.map(conv => ({
            Phone: conv.phone,
            Name: conv.name || 'Unknown',
            'Last Message': conv.lastMessage?.content?.text || 'N/A',
            'Last Message Time': new Date(conv.lastMessage?.timestamp).toLocaleString(),
            'Messages Count': conv.messagesCount || 0,
            Status: conv.lastMessage?.status || 'N/A'
        })));
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Conversations');
        XLSX.writeFile(workbook, 'conversations.xlsx');
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        
        const tableData = conversations.map(conv => [
            conv.phone,
            conv.name || 'Unknown',
            conv.lastMessage?.content?.text || 'N/A',
            new Date(conv.lastMessage?.timestamp).toLocaleString(),
            conv.messagesCount || 0,
            conv.lastMessage?.status || 'N/A'
        ]);
        
        autoTable(doc, {
            head: [['Phone', 'Name', 'Last Message', 'Last Message Time', 'Messages Count', 'Status']],
            body: tableData,
        });
        
        doc.save('conversations.pdf');
    };

    const handleExportConversation = async (conversation) => {
        try {
            const response = await apiService.getChatHistory(conversation.phone, 1, 1000);
            const messages = response.messages || [];
            
            let text = `Conversation with ${conversation.name || conversation.phone}\n`;
            text += `========================================\n\n`;
            
            messages.forEach(msg => {
                const time = new Date(msg.timestamp).toLocaleString();
                const sender = msg.direction === 'outgoing' ? 'Agent' : conversation.name || conversation.phone;
                let content = '';
                
                if (msg.type === 'text_message') {
                    content = msg.content?.text || '';
                } else if (msg.type === 'image_message') {
                    content = `[Image] ${msg.content?.caption || ''}`;
                } else if (msg.type === 'video_message') {
                    content = `[Video] ${msg.content?.caption || ''}`;
                } else if (msg.type === 'audio_message') {
                    content = `[Audio Message]`;
                } else if (msg.type === 'document_message') {
                    content = `[Document] ${msg.content?.filename || ''}`;
                } else if (msg.type === 'template_message') {
                    content = `[Template] ${msg.content?.template?.name || ''}`;
                }
                
                text += `${time} - ${sender}: ${content}\n`;
            });
            
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `conversation-${conversation.phone}.txt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
        } catch (err) {
            console.error('Error exporting conversation:', err);
            alert('Failed to export conversation');
        }
    };

    const columns = [
        {
            key: 'phone',
            label: 'Phone Number',
            render: (row) => <span className="font-medium text-gray-900">{row.phone}</span>
        },
        {
            key: 'name',
            label: 'Name',
            render: (row) => <span>{row.name || 'Unknown'}</span>
        },
        {
            key: 'lastMessage',
            label: 'Last Message',
            render: (row) => {
                if (!row.lastMessage) return <span className="text-gray-500">No messages</span>;
                
                let content = '';
                if (row.lastMessage.type === 'text_message') {
                    content = row.lastMessage.content?.text || '';
                } else if (row.lastMessage.type === 'image_message') {
                    content = `[Image] ${row.lastMessage.content?.caption || ''}`;
                } else if (row.lastMessage.type === 'video_message') {
                    content = `[Video] ${row.lastMessage.content?.caption || ''}`;
                } else if (row.lastMessage.type === 'audio_message') {
                    content = `[Audio Message]`;
                } else if (row.lastMessage.type === 'document_message') {
                    content = `[Document]`;
                } else if (row.lastMessage.type === 'template_message') {
                    content = `[Template] ${row.lastMessage.content?.template?.name || ''}`;
                }
                
                return <span className="truncate max-w-xs block">{content}</span>;
            }
        },
        {
            key: 'lastMessageTime',
            label: 'Last Activity',
            render: (row) => {
                if (!row.lastMessage?.timestamp) return <span>-</span>;
                return <span>{new Date(row.lastMessage.timestamp).toLocaleString()}</span>;
            }
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (row) => (
                <div className="flex space-x-2">
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewConversation(row);
                        }}
                    >
                        View
                    </Button>
                    <Button 
                        variant="outline" 
                        size="sm"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleExportConversation(row);
                        }}
                    >
                        <FileText size={16} />
                    </Button>
                </div>
            )
        }
    ];

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Conversations Report</h1>
                <div className="flex space-x-2">
                    <Button onClick={handleExportExcel} variant="outline">
                        <FileDown size={16} className="mr-2" />
                        Export Excel
                    </Button>
                    <Button onClick={handleExportPDF} variant="outline">
                        <Download size={16} className="mr-2" />
                        Export PDF
                    </Button>
                </div>
            </div>
            
            <Card className="mb-6">
                <div className="p-4">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search size={18} className="text-gray-400" />
                            </div>
                            <input
                                type="text"
                                className="pl-10 pr-10 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Search by name or phone number..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={handleSearch}
                            />
                            {searchQuery && (
                                <button
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={clearSearch}
                                >
                                    <X size={18} className="text-gray-400" />
                                </button>
                            )}
                        </div>
                        
                        <Button
                            variant="outline"
                            onClick={() => setShowFilters(!showFilters)}
                            className="md:w-auto w-full"
                        >
                            <Filter size={16} className="mr-2" />
                            Filters
                            <ChevronDown size={16} className="ml-2" />
                        </Button>
                        
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-600">Show:</span>
                            <select
                                className="border border-gray-300 rounded-md px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={pageSize}
                                onChange={handlePageSizeChange}
                            >
                                <option value="50">50</option>
                                <option value="100">100</option>
                                <option value="200">200</option>
                                <option value="500">500</option>
                            </select>
                        </div>
                    </div>
                    
                    {showFilters && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                <input
                                    type="date"
                                    className="border border-gray-300 rounded-md px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Card>
            
            {loading ? (
                <div className="flex justify-center items-center h-64">
                    <Loading />
                </div>
            ) : error ? (
                <div className="text-center text-red-600 p-4">{error}</div>
            ) : (
                <>
                    <Table
                        columns={columns}
                        data={conversations}
                        onRowClick={handleViewConversation}
                        pagination={{
                            currentPage: page,
                            pageSize,
                            total,
                            onNextPage: handleNextPage,
                            onPrevPage: handlePrevPage
                        }}
                    />
                    
                    {conversations.length === 0 && (
                        <div className="text-center text-gray-500 p-8">
                            No conversations found
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ConversationsReport; 